import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import AuthLayout from "@/components/auth/AuthLayout";
import AuthHeader from "@/components/auth/AuthHeader";
import PasswordInput from "@/components/auth/PasswordInput";
import Divider from "@/components/auth/Divider";
import GoogleButton from "@/components/auth/GoogleButton";

const SignUpStepOne = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { signUp, signInWithGoogle, user } = useAuth();
    const [loading, setLoading] = useState(false);

    const redirectTo = searchParams.get('redirect');

    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        email: "",
        password: "",
        confirmPassword: "",
    });

    useEffect(() => {
        if (user) {
            navigate(redirectTo || '/', { replace: true });
        }
    }, [user, navigate, redirectTo]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (formData.password !== formData.confirmPassword) {
            toast({
                title: "Passwords don't match",
                description: "Please make sure both passwords are the same.",
                variant: "destructive",
            });
            return;
        }

        if (formData.password.length < 6) {
            toast({
                title: "Password too short",
                description: "Password must be at least 6 characters long.",
                variant: "destructive",
            });
            return;
        }

        setLoading(true);

        try {
            // Detect country once at signup time — no extra calls on subsequent logins
            let detectedCountry: string | undefined;
            try {
                const geoRes = await fetch('https://ipapi.co/json/');
                if (geoRes.ok) {
                    const geoData = await geoRes.json();
                    detectedCountry = geoData.country_code ?? undefined;
                }
            } catch {
                // Geolocation failed silently — signup will still proceed
            }

            const { error } = await signUp(formData.email, formData.password, {
                firstName: formData.firstName,
                lastName: formData.lastName,
                country: detectedCountry,
                //  });
            }, redirectTo || undefined);

            if (error) {
                if (error.message.includes('User already registered')) {
                    toast({
                        title: "Account already exists",
                        description: "This email is already registered. Please sign in instead.",
                        variant: "destructive",
                        action: <ToastAction altText="Sign In" onClick={() => navigate("/auth")}>Sign In</ToastAction>,
                    });
                } else {
                    toast({
                        title: "Registration failed",
                        description: error.message,
                        variant: "destructive",
                    });
                }
            } else {
                toast({
                    title: "Registration successful",
                    description: "Please check your email to confirm your account.",
                });
            }
        } catch {
            toast({
                title: "An error occurred",
                description: "Please try again later.",
                variant: "destructive",
            });
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleSignIn = async () => {
        // const { error } = await signInWithGoogle();
        const { error } = await signInWithGoogle(redirectTo || undefined);
        if (error) {
            toast({
                title: "Google sign in failed",
                description: error.message,
                variant: "destructive",
            });
        }
    };

    return (
        <AuthLayout>
            <div className="flex flex-col min-h-[70vh]">
                <AuthHeader />

                <h2 className="text-[28px] font-semibold text-center">
                    Create a free Opsero account
                </h2>

                <form onSubmit={handleSubmit}>
                    {/* Name Row */}
                    <div className="grid grid-cols-2 gap-4 mt-8 mb-6">
                        <Input
                            name="firstName"
                            placeholder="First Name"
                            value={formData.firstName}
                            onChange={handleChange}
                            required
                        />
                        <Input
                            name="lastName"
                            placeholder="Last Name"
                            value={formData.lastName}
                            onChange={handleChange}
                            required
                        />
                    </div>

                    <p className="text-sm font-medium text-gray-700 mb-2">
                        Business Email
                    </p>
                    <Input
                        name="email"
                        type="email"
                        placeholder="Enter your Business Email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                    />

                    <p className="text-sm font-medium text-gray-700 mt-4 mb-2">
                        Password
                    </p>
                    <PasswordInput
                        name="password"
                        placeholder="******"
                        value={formData.password}
                        onChange={handleChange}
                        required
                    />

                    <p className="text-sm font-medium text-gray-700 mt-4 mb-2">
                        Confirm Password
                    </p>
                    <PasswordInput
                        name="confirmPassword"
                        placeholder="******"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        required
                    />

                    <Button
                        type="submit"
                        disabled={loading}
                        className="w-full mt-7 h-12 text-lg font-semibold bg-[#7C3AED] hover:bg-[#6D28D9] text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {loading ? (
                            <LoadingSpinner size="small" iconClassName="text-white" label="Creating account..." />
                        ) : (
                            'Next'
                        )}
                    </Button>

                    <div className="text-center mt-3 mb-6">
                        <span className="text-[16px] font-medium text-black">
                            Already have an account?{" "}
                        </span>
                        <button
                            type="button"
                            onClick={() => navigate("/auth")}
                            className="text-[16px] text-[#7C3AED] font-medium hover:underline"
                        >
                            Sign In
                        </button>
                    </div>

                    <Divider />

                    <div className="mt-6">
                        <GoogleButton
                            text="Continue with Google"
                            onClick={handleGoogleSignIn}
                        />
                    </div>

                    <div className="mt-auto pt-10">
                        <p className="text-sm text-gray-500 text-center">
                            By signing up, you agree to our{" "}
                            <span className="underline cursor-pointer">
                                Terms
                            </span>{" "}
                            and{" "}
                            <span className="underline cursor-pointer">
                                Privacy Policy
                            </span>.
                        </p>
                    </div>
                </form>
            </div>
        </AuthLayout>
    );
};

export default SignUpStepOne;
